import { dayKey, instantToWallTime, wallTimeToInstant } from '../../utils/zonedTime';
import { formatDateTime } from '../../utils/format';

export function formatTodoDate(dateString: string, language: string): string {
  return formatDateTime(dateString, language, { month: 'long' });
}

export function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

export function formatTotalTime(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

const pad2 = (n: number) => n.toString().padStart(2, '0');

/** Calendar day in the account's time zone as YYYY-MM-DD (the backend stores due dates as plain days). */
export function toLocalDateString(date: Date = new Date()): string {
  return dayKey(date);
}

const dayNumber = (day: string): number => {
  const [y, m, d] = day.split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / 86_400_000);
};

/** YYYY-MM-DD shifted by a number of days. */
export function addDays(day: string, days: number): string {
  const [y, m, d] = day.split('-').map(Number);
  const shifted = new Date(Date.UTC(y, m - 1, d + days));
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`;
}

export type DueState = 'overdue' | 'today' | 'tomorrow' | 'upcoming';

export interface DueDescription {
  state: DueState;
  /** Whole days between the due day and today, always >= 0 */
  days: number;
}

export function describeDue(dueDate: string, today: string): DueDescription {
  const diff = dayNumber(dueDate) - dayNumber(today);
  if (diff < 0) return { state: 'overdue', days: -diff };
  if (diff === 0) return { state: 'today', days: 0 };
  if (diff === 1) return { state: 'tomorrow', days: 1 };
  return { state: 'upcoming', days: diff };
}

export function isOverdue(dueDate: string | null | undefined, completed: boolean | undefined, today: string): boolean {
  return Boolean(dueDate) && !completed && describeDue(dueDate as string, today).state === 'overdue';
}

/** ISO instant -> value for <input type="datetime-local"> in the account's time zone. */
export function toDateTimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return instantToWallTime(date);
}

/** <input type="datetime-local"> value -> ISO instant (UTC); empty string clears. */
export function fromDateTimeLocalValue(value: string): string | null {
  if (!value) return null;
  const date = wallTimeToInstant(value);
  return date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
}

/** "1h 30m", "45m" or "0m" — compact, language-neutral like formatDuration. */
export function formatHoursMinutes(seconds: number): string {
  const total = Math.max(0, Math.round(seconds / 60));
  const hrs = Math.floor(total / 60);
  const mins = total % 60;
  if (hrs > 0) return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
  return `${mins}m`;
}
